const NAL_NAMES = { 1: 'P', 5: 'I', 7: 'SPS', 8: 'PPS' }
const NAL_ORDER = { 1: 4, 5: 3, 7: 1, 8: 2 }

const SORTED_ASPECT_RATIOS = ['9:32', '1:2.39', '1:2.35', '9:21', '1:1.85', '9:16', '10:16', '2:3', '3:4', '4:5', '1:1', '5:4', '4:3', '3:2', '16:10', '16:9', '1.85:1', '21:9', '2.35:1', '2.39:1', '32:9'].map(x=>{ const [a, b]=x.split(':'); return [Number((a/b).toFixed(3)), x] })
const LOOKUP_ASPECT_RATIOS = Object.fromEntries(SORTED_ASPECT_RATIOS)

export class NAL extends Uint8Array {
  // .ets = event timestamp - "optical" time of NAL in miliseconds
  // .gts = gathered timestamp - time when NAL arrived to mBox
  // .its = issued timestamp - time when NAL was published in mBox
  // .lts = load timestamp - time when NAL arrived to player
  // .mts = move timestamp - time when NAL was pushed to source buffer
  // .pts = presentation timestamp - offset of NAL event time since first played NAL
  // .rts = real timstamp - real time in player
  static P = 1
  static I = 5
  static SPS = 7
  static PPS = 8
  get type() { return(this[0]&31) }
  get name() { return NAL_NAMES[this[0]&31] }
  get frame() { const type = this[0]&31; return(type==1 || type==5) }
  get keyframe() { return((this[0]&31)==5) }
  get order() { return(NAL_ORDER[this[0]&31] || 0) }
  static msec2time(msec) {
    const d = new Date(msec)
    if(isNaN(d.valueOf())) return ''
    return(
      String(d.getUTCHours()).padStart(2, 0)+':'+
      String(d.getUTCMinutes()).padStart(2, 0)+':'+
      String(d.getUTCSeconds()).padStart(2, 0)+'.'+
      String(msec%1_000).padStart(3, 0)
    )
  }

  static msec2duration(msec) {
    if(isNaN(msec)) return ''
    const minus = msec<0
    if(minus) msec=-msec
    const h = Math.floor(msec/3_600_000)
    const m = Math.floor((msec%3_600_000)/60_000)
    const s = Math.floor((msec%60_000)/1_000)
    const ms = msec%1_000
    return(
      (minus ? '-' : '') +
      (h>0?h+':':'') +
      String(m).padStart(2, 0)+':'+
      String(s).padStart(2, 0)+'.'+
      String(ms).padStart(3, 0)
    )
  }

  toString() {
    const parts = []
    if(!isNaN(this.ets)) parts.push(`[${NAL.msec2time(this.ets)}]`)
    if(!isNaN(this.pts)) parts.push(NAL.msec2duration(this.pts))
    if(!isNaN(this.duration)) {
      if(isNaN(this.pts)) parts.push(this.duration+'ms')
      else parts[parts.length-1] += ' ~ '+NAL.msec2duration(this.pts+this.duration)
    }
    parts.push(this.name)
    if(this.id>0) parts[parts.length-1] += `(${this.id})`
    return parts.join(' ')
  }

  static compare(a, b) {
    return a.ets-b.ets || NAL_ORDER[a.type]-NAL_ORDER[b.type]
  }

  equals(nal) {
    return(
      nal &&
      this.byteLength == nal.byteLength &&
      this.every((val, i)=>val==nal[i])
    )
  }

  static aspectRatio(w, h) {
    const ratio = Math.max(w, h) / Math.min(w, h)
    let name = LOOKUP_ASPECT_RATIOS[ratio]
    if(!name) {
      name = SORTED_ASPECT_RATIOS[0][1]
      let closestDiff = Math.abs(SORTED_ASPECT_RATIOS[0][0] - ratio)
      for(let i=1; i<SORTED_ASPECT_RATIOS.length; i++) {
        const diff = Math.abs(SORTED_ASPECT_RATIOS[i][0] - ratio)
        if(diff<closestDiff) {
          name = SORTED_ASPECT_RATIOS[i][1]
          closestDiff = diff
        }else{
          break
        }
      }
    }
    if(w<h) name = name.replace(/(.+):(.+)/, '$2:$1')
    return name
  } // aspectRatio
}

export class NALs extends Array {
  toString() {
    return this.map(nal=>nal.name).join(' ')
  }

  sort() {
    return super.sort(NAL.compare)
  }

  stripFramesBeforeKeyframe() {
    let idx = this.findIndex(nal=>nal.type==NAL.I)
    if(idx<=0) return 0
    let deleted = 0
    idx--
    while(idx>=0) {
      if(this[idx].type==NAL.P) {
        this.splice(idx, 1)
        deleted++
      }
      idx--
    }
    return deleted
  }
}

export function uint8ToNAL(uint) {
  if(uint instanceof Uint8Array) Object.setPrototypeOf(uint, NAL.prototype)
  return uint
}
